import { useEffect, useState } from 'react';
import { prefs } from '../db/prefs';
import { clearContent, resetDevice } from '../db/maintenance';
import { getStorageEstimate } from '../db/storage';
import { forceUpdate, isUpdateReady } from '../pwa/register';
import { toggleFullscreen } from '../helpers/fullscreen';
import { APP_VERSION } from '../config/env';
import { useI18n } from '../i18n/I18nContext';

type UpdateState = 'idle' | 'checking' | 'available' | 'applying' | 'current';

function fmtBytes(n?: number): string {
  if (n == null) return '—';
  const mb = n / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GiB` : `${mb.toFixed(0)} MiB`;
}

interface DeviceSettingsProps {
  onClose: () => void;
}

type Pending = 'clear' | 'reset' | null;

// In-player settings, opened via a hidden gesture, so an on-site installer can
// recover a screen (re-sync, unpair, fullscreen) without browser access.
export function DeviceSettings({ onClose }: DeviceSettingsProps) {
  const { t } = useI18n();
  const [uuid, setUuid] = useState('');
  const [deviceId, setDeviceId] = useState(0);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [storage, setStorage] = useState<{ persisted: boolean; usage?: number; quota?: number }>({
    persisted: false,
  });
  const [updateState, setUpdateState] = useState<UpdateState>(
    isUpdateReady() ? 'available' : 'idle',
  );

  useEffect(() => {
    void (async () => {
      setUuid(await prefs.getUuid());
      setDeviceId(await prefs.getDeviceId());
      setStorage(await getStorageEstimate());
    })();
  }, []);

  // Manually pull/apply a new build, bypassing the update_preference window so an
  // on-site installer can force the latest version without DevTools. Shares the
  // same logic as the `app.force_update` socket event; reloads on success.
  const runUpdate = async () => {
    setUpdateState(isUpdateReady() ? 'applying' : 'checking');
    const applied = await forceUpdate();
    if (!applied) setUpdateState('current');
  };

  const updateLabel = (() => {
    switch (updateState) {
      case 'checking':
        return t('settings.checkingUpdate');
      case 'available':
        return t('settings.applyUpdate');
      case 'applying':
        return t('settings.applyingUpdate');
      case 'current':
        return t('settings.upToDate');
      default:
        return t('settings.checkUpdate');
    }
  })();
  const updateBusy = updateState === 'checking' || updateState === 'applying';

  const runClear = async () => {
    setBusy(true);
    await clearContent();
    window.location.reload();
  };

  const runReset = async () => {
    setBusy(true);
    await resetDevice();
    window.location.reload();
  };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">{t('settings.title')}</div>

        <div className="settings-info">
          <div>
            {t('settings.device')}: <b>#{deviceId || '—'}</b>
          </div>
          <div className="settings-uuid">{uuid || '—'}</div>
          <div>
            {t('settings.version')}: <b>{APP_VERSION}</b>
          </div>
          <div>
            Storage:{' '}
            <b style={{ color: storage.persisted ? 'inherit' : 'var(--vz-danger, #e5484d)' }}>
              {storage.persisted ? 'persisted' : 'best-effort'}
            </b>{' '}
            <span className="settings-uuid">
              {fmtBytes(storage.usage)} / {fmtBytes(storage.quota)}
            </span>
          </div>
        </div>

        <div className="settings-actions">
          <button className="settings-btn" onClick={() => void toggleFullscreen()}>
            {t('settings.fullscreen')}
          </button>
          <button className="settings-btn" onClick={() => window.location.reload()}>
            {t('settings.reload')}
          </button>
          <button className="settings-btn" onClick={() => void runUpdate()} disabled={updateBusy}>
            {updateLabel}
          </button>

          {pending === 'clear' ? (
            <ConfirmRow
              label={t('settings.confirmClear')}
              busy={busy}
              onConfirm={() => void runClear()}
              onCancel={() => setPending(null)}
              cancelLabel={t('common.cancel')}
            />
          ) : (
            <button className="settings-btn" onClick={() => setPending('clear')}>
              {t('settings.clearContent')}
            </button>
          )}

          {pending === 'reset' ? (
            <ConfirmRow
              label={t('settings.confirmReset')}
              busy={busy}
              danger
              onConfirm={() => void runReset()}
              onCancel={() => setPending(null)}
              cancelLabel={t('common.cancel')}
            />
          ) : (
            <button className="settings-btn danger" onClick={() => setPending('reset')}>
              {t('settings.reset')}
            </button>
          )}
        </div>

        <button className="pairing-retry" onClick={onClose}>
          {t('settings.close')}
        </button>
      </div>
    </div>
  );
}

interface ConfirmRowProps {
  label: string;
  cancelLabel: string;
  busy: boolean;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmRow({ label, cancelLabel, busy, danger, onConfirm, onCancel }: ConfirmRowProps) {
  return (
    <div className="settings-confirm">
      <div className="settings-confirm-text">{label}</div>
      <div className="settings-confirm-buttons">
        <button
          className={`settings-btn ${danger ? 'danger' : ''}`}
          onClick={onConfirm}
          disabled={busy}
        >
          {'✓'}
        </button>
        <button className="settings-btn" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </button>
      </div>
    </div>
  );
}
